import { Button, Modal } from "react-bootstrap";

const AvailabilityExplainModal = ({
  show,
  onHide,
}: {
  show: boolean;
  onHide: () => void;
}) => (
  <Modal show={show} onHide={onHide} centered size="lg">
    <Modal.Header closeButton>
      <Modal.Title>PV System Availability</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        PV system availability measures what proportion of expected generation
        time the system was actually operational.
      </p>
      <p>
        <strong>availability = (T &minus; O) / T</strong> (headline, all-cause)
        <br />
        <strong>availability = (T &minus; O) / (T &minus; C)</strong>{" "}
        (&ldquo;excused&rdquo;, the EPC figure)
      </p>
      <p>
        where <strong>T</strong> is the expected generation periods (the
        &ldquo;clock&rdquo;), <strong>O</strong> the periods lost to outages,
        and <strong>C</strong> the periods lost to <em>excused</em> (Excludable
        Event) outages. The headline figure docks every outage. The excused
        figure removes excused periods from <em>both</em> the numerator and the
        denominator &mdash; they neither raise nor lower it &mdash; matching the
        Amazon EUSDR &ldquo;remove-from-clock&rdquo; treatment. With no excused
        outages the two are equal.
      </p>
      <p>
        <strong>Per inverter.</strong> Plant availability is the mean of the
        individual inverters&apos; availabilities: a per-inverter outage docks
        only that inverter, while a whole-site outage (e.g. a site-wide
        zero-generation event) docks every inverter. When no per-inverter
        information is available, the whole plant is graded as one.
      </p>
      <p>
        <strong>Expected generation periods</strong> are determined from the
        site&apos;s P50 generation forecast, which originates from hourly PVGIS
        Typical Meteorological Year (TMY) data for the site&apos;s location. To
        smooth out day-to-day TMY variation, each day&apos;s expected generation
        profile is computed as the median of a rolling 7-day window of
        neighbouring days&apos; forecasts.
      </p>
      <p>
        Hours where the smoothed forecast exceeds a capacity-scaled threshold (1
        kWh per 100 kWp of installed capacity) are considered expected
        generation hours &mdash; hours below this threshold are too low to
        reliably expect generation. For each day&apos;s first expected
        generation hour, only the last measurement period within that hour is
        counted (e.g. the last 30 minutes for a half-hourly site, or the last 15
        minutes for a quarter-hourly site), since the system may still be
        ramping up. The same applies in reverse for the last expected generation
        hour of each day &mdash; only the first measurement period is counted.
        All other expected generation hours are counted at the site&apos;s full
        measurement resolution.
      </p>
      <p>
        <strong>Outage periods (O)</strong> are the total duration of
        &ldquo;zero generation&rdquo; health events classified as an{" "}
        <em>unplanned PV outage</em>. An <em>unplanned PV outage (excused)</em>{" "}
        &mdash; an Excludable Event such as a grid/network outage, force
        majeure, or a third-party cause an admin has marked as excused &mdash;
        is the <strong>excused periods (C)</strong> above: removed from both
        sides of the ratio. Events classified as{" "}
        <em>unplanned battery outage</em>, <em>planned</em> (e.g. scheduled
        maintenance), <em>transient</em> (auto-resolved), or <em>ignore</em> are
        excluded from the calculation entirely. For sites with multiple energy
        matches, zero generation is detected independently per energy match and
        the unavailable time ranges are combined (unioned) for the site&apos;s
        overall availability.
      </p>
      <p className="text-muted small mb-0">
        &ldquo;Last month&rdquo; covers the previous complete calendar month.
        &ldquo;Last 12 months&rdquo; covers the trailing 12 complete calendar
        months.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

export default AvailabilityExplainModal;
